import axios from "axios";
import store from "../store";
import {
  getOrStartRefresh,
  isAuthInvalidated,
} from "../auth/refreshCoordinator";
import { getProductionAuthPersistence } from "../persistence/authPersistenceProduction";
import { API_BASE_URL } from "./apiBase";

/** @param {string} url */
function requestPathname(url) {
  if (!url) return "";
  const withoutQuery = String(url).split("?")[0];
  if (!withoutQuery.startsWith("/")) return withoutQuery;
  return withoutQuery.endsWith("/") ? withoutQuery : `${withoutQuery}/`;
}

const AUTH_BOOTSTRAP_PATHS = [
  "/users/login/",
  "/users/register/",
  "/users/google/login/",
];

/** Read-only KATOTTG catalog — backend AllowAny (apps.geo.views). */
const PUBLIC_CATALOG_PREFIX = "/geo/";

/** Authenticated Property catalog reads. Anonymous 401 must not refresh. */
function isPublicReadonlyRealtyCatalogRequest(url, method) {
  const normalizedMethod = String(method || "get").toLowerCase();
  if (normalizedMethod !== "get") {
    return false;
  }
  const pathname = requestPathname(url);
  if (pathname === "/realty/properties/") {
    return true;
  }
  return /^\/realty\/properties\/\d+\/$/.test(pathname);
}

/** PROPERTY-VIEW-TRACKING-A1: best-effort view POST is not a stale-session signal. */
function isPublicPropertyViewLogRequest(url, method) {
  const normalizedMethod = String(method || "get").toLowerCase();
  if (normalizedMethod !== "post") {
    return false;
  }
  return /^\/realty\/properties\/\d+\/view\/$/.test(requestPathname(url));
}

const PRESENTATION_SHARE_PATH =
  /(?:^|\/)(?:api\/(?:v1\/)?)?realty\/shares\/[0-9a-fA-F-]{36}\/(?:events\/|view\/)?$/;

/** CLIENT-PRESENTATION-LINKS-C1: public token fetch/events are not stale-session. */
function isPublicPresentationRequest(url, method) {
  const normalizedMethod = String(method || "get").toLowerCase();
  if (normalizedMethod !== "get" && normalizedMethod !== "post") {
    return false;
  }
  const pathname = requestPathname(url);
  if (PRESENTATION_SHARE_PATH.test(pathname)) {
    return true;
  }
  return PRESENTATION_SHARE_PATH.test(String(url || ""));
}

/**
 * P-1E-R2: only GET /geo/* catalog reads are refresh-ineligible.
 * Authenticated geo writes (e.g. POST /geo/map-link/coordinates/) remain eligible.
 */
function isPublicReadonlyGeoCatalogRequest(url, method) {
  const normalizedMethod = String(method || "get").toLowerCase();
  if (normalizedMethod !== "get") {
    return false;
  }
  const pathname = requestPathname(url);
  return pathname.startsWith(PUBLIC_CATALOG_PREFIX);
}

/**
 * P-1E-R1: 401 refresh must not run for auth-bootstrap or refresh endpoints.
 * P-1E-R2: public geography catalog GET excluded — 401 is not stale-session semantics.
 * G1-R1 / B2: Property catalog GET is authenticated-only at the API.
 * Anonymous 401 (no Authorization) is still not a refresh signal.
 * PRE-ROADMAP-STAGING-QA-CORRECTION-A1: if Authorization was attached, a 401
 * on those same GETs is a stale-session signal and is refresh-eligible.
 * PROPERTY-VIEW-TRACKING-A1: best-effort POST /realty/properties/{id}/view/
 * 401 must not refresh or invalidate the loaded detail session.
 * CLIENT-PRESENTATION-LINKS-C1: public GET/POST /realty/shares/{token}/
 * (and events/view aliases) 401 must not refresh or logout.
 * Relative paths match axios baseURL-relative config.url values.
 */
function isRefreshIneligibleUrl(url, method) {
  const pathname = requestPathname(url);
  if (pathname.includes("/token/refresh")) {
    return true;
  }
  if (isPublicReadonlyGeoCatalogRequest(url, method)) {
    return true;
  }
  if (isPublicReadonlyRealtyCatalogRequest(url, method)) {
    return true;
  }
  if (isPublicPropertyViewLogRequest(url, method)) {
    return true;
  }
  if (isPublicPresentationRequest(url, method)) {
    return true;
  }
  if (AUTH_BOOTSTRAP_PATHS.includes(pathname)) {
    return true;
  }
  if (pathname.startsWith("/users/password-reset/")) {
    return true;
  }
  return false;
}

function requestHadAuthorization(originalRequest) {
  const headers = originalRequest?.headers || {};
  return Boolean(headers.Authorization || headers.authorization);
}

function isEligible401Refresh(error, originalRequest) {
  const status = error.response?.status;
  const url = originalRequest?.url || "";
  const method = originalRequest?.method || "get";
  if (status !== 401 || originalRequest?.__isRetry) {
    return false;
  }
  const hadAuth = requestHadAuthorization(originalRequest);
  // Stale attached access token on public catalog reads is a refresh signal.
  // Anonymous requests (no Authorization) remain ineligible.
  if (hadAuth && isPublicReadonlyRealtyCatalogRequest(url, method)) {
    return true;
  }
  if (hadAuth && isPublicReadonlyGeoCatalogRequest(url, method)) {
    return true;
  }
  return !isRefreshIneligibleUrl(url, method);
}

// R7.9b — repeated-key array serialization for axios params.
//
// axios v1.x default emits arrays as `key[]=v1&key[]=v2`, but Django's
// QueryDict treats `key[]` as a distinct key from `key`, so
// django-filter's MultipleChoiceFilter (which reads getlist("key"))
// returns [] for bracketed names. Without this serializer, every
// array-valued backend filter — most importantly `?property_type=` on
// /realty/properties/ — silently no-ops.
//
// This is transport-truth repair, NOT a query helper. Repeated keys
// for arrays; skip null/undefined/empty values; cast booleans to
// "true"/"false" (matches BooleanFilter parsing).
const realtyParamsSerializer = (params) => {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params || {})) {
    if (value === null || value === undefined || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === null || item === undefined || item === "") continue;
        usp.append(key, String(item));
      }
    } else if (typeof value === "boolean") {
      usp.append(key, value ? "true" : "false");
    } else {
      usp.append(key, String(value));
    }
  }
  return usp.toString();
};

const api = axios.create({
  baseURL: API_BASE_URL,
  paramsSerializer: { serialize: realtyParamsSerializer },
});

api.interceptors.request.use((config) => {
  const access = getProductionAuthPersistence().readAccessToken();
  if (access) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest || !isEligible401Refresh(error, originalRequest)) {
      return Promise.reject(error);
    }

    originalRequest.__isRetry = true;

    try {
      const newAccess = await getOrStartRefresh(store.dispatch);

      if (isAuthInvalidated()) {
        return Promise.reject(new Error("Auth invalidated during refresh"));
      }

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      return api(originalRequest);
    } catch (refreshError) {
      // P-1E: superseded, terminated, and clear-failure outcomes propagate
      // without retry — coordinator owns terminal session invalidation.
      return Promise.reject(refreshError);
    }
  }
);

export default api;
